import { c as _c } from "react/compiler-runtime";
import { DonationIntervals, PaymentMethods } from 'api-graphql-types';
import { useDonationFormContext, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import { useEffect, type ChangeEvent } from 'react';
import { FeatureFlagKey } from 'product-analytics';
import { noop } from 'betterplace-utils';
import useFeatureFlagMatches from '@/analytics/featureFlags/useFeatureFlagMatches';
const controlGroupNoopObject = {
  showAutoImportToggle: false,
  showDataImportAlert: false,
  clearFieldsOnDataImportEnabled: noop,
  onCompanyDonationToggle: noop,
  onPaymentMethodChange: noop,
  onReceiptDesiredToggle: noop,
  onIntervalSelectionChange: noop
};
function useDataAutoImport() {
  const $ = _c(33);
  const {
    setValue
  } = useDonationFormContext();
  const controlGroup = useFeatureFlagMatches(FeatureFlagKey.ExpressCheckout, "control");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["payment_method", "interval", "receipt_desired", "import_data_from_psp", "_company_donation"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [paymentMethod, interval, receiptDesired, importDataFromPsp, companyDonation] = useDonationFormValues(t0);
  const showAutoImportToggle = paymentMethod === PaymentMethods.Paypal && interval === DonationIntervals.Single && !companyDonation;
  const showDataImportAlert = importDataFromPsp && paymentMethod === PaymentMethods.Paypal && interval === DonationIntervals.Single && !companyDonation;
  let t1;
  if ($[1] !== setValue) {
    t1 = function clearInputs() {
      setValue("street", "", {
        shouldValidate: true
      });
      setValue("zip", "", {
        shouldValidate: true
      });
      setValue("city", "", {
        shouldValidate: true
      });
      setValue("email", "", {
        shouldValidate: true
      });
      setValue("first_name", "", {
        shouldValidate: true
      });
      setValue("last_name", "", {
        shouldValidate: true
      });
    };
    $[1] = setValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const clearInputs = t1;
  let t2;
  if ($[3] !== clearInputs || $[4] !== setValue) {
    t2 = function clearFieldsOnDataImportEnabled(e) {
      if (!e.currentTarget.checked) {
        setValue("receipt_desired", false, {
          shouldValidate: true
        });
        return;
      }
      setValue("receipt_desired", true, {
        shouldValidate: true
      });
      clearInputs();
    };
    $[3] = clearInputs;
    $[4] = setValue;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const clearFieldsOnDataImportEnabled = t2;
  let t3;
  if ($[6] !== companyDonation || $[7] !== interval || $[8] !== paymentMethod || $[9] !== receiptDesired || $[10] !== setValue) {
    t3 = function onCompanyDonationToggle() {
      if (!companyDonation && !receiptDesired) {
        setValue("receipt_desired", true);
      }
      if (companyDonation && receiptDesired) {
        setValue("receipt_desired", false);
      }
      if (paymentMethod !== PaymentMethods.Paypal || interval !== DonationIntervals.Single) {
        return;
      }
      if (companyDonation) {
        setValue("import_data_from_psp", true, {
          shouldValidate: true
        });
        return;
      }
      if (!companyDonation) {
        setValue("import_data_from_psp", false, {
          shouldValidate: true
        });
      }
    };
    $[6] = companyDonation;
    $[7] = interval;
    $[8] = paymentMethod;
    $[9] = receiptDesired;
    $[10] = setValue;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const onCompanyDonationToggle = t3;
  let t4;
  if ($[12] !== companyDonation || $[13] !== importDataFromPsp || $[14] !== interval || $[15] !== setValue) {
    t4 = function onPaymentMethodChange(name) {
      if (interval !== DonationIntervals.Single || companyDonation) {
        return;
      }
      if (name !== PaymentMethods.Paypal && importDataFromPsp) {
        setValue("import_data_from_psp", false, {
          shouldValidate: true
        });
        setValue("receipt_desired", false, {
          shouldValidate: true
        });
        return;
      }
      if (name === PaymentMethods.Paypal && !importDataFromPsp) {
        setValue("import_data_from_psp", true, {
          shouldValidate: true
        });
        setValue("receipt_desired", true, {
          shouldValidate: true
        });
      }
    };
    $[12] = companyDonation;
    $[13] = importDataFromPsp;
    $[14] = interval;
    $[15] = setValue;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const onPaymentMethodChange = t4;
  let t5;
  if ($[17] !== setValue) {
    t5 = function onReceiptDesiredToggle(e_0) {
      if (!e_0.currentTarget.checked) {
        return;
      }
      setValue("street", "", {
        shouldValidate: true
      });
      setValue("zip", "", {
        shouldValidate: true
      });
      setValue("city", "", {
        shouldValidate: true
      });
    };
    $[17] = setValue;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const onReceiptDesiredToggle = t5;
  let t6;
  if ($[19] !== clearInputs || $[20] !== companyDonation || $[21] !== importDataFromPsp || $[22] !== paymentMethod || $[23] !== setValue) {
    t6 = function onIntervalSelectionChange(value) {
      if (paymentMethod !== PaymentMethods.Paypal || companyDonation) {
        return;
      }
      if (value === DonationIntervals.Single) {
        setValue("import_data_from_psp", true, {
          shouldValidate: true
        });
        setValue("receipt_desired", true, {
          shouldValidate: true
        });
        if (importDataFromPsp) {
          clearInputs();
        }
        return;
      }
      if (importDataFromPsp) {
        setValue("import_data_from_psp", false);
        setValue("receipt_desired", false);
      }
    };
    $[19] = clearInputs;
    $[20] = companyDonation;
    $[21] = importDataFromPsp;
    $[22] = paymentMethod;
    $[23] = setValue;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const onIntervalSelectionChange = t6;
  if (controlGroup) {
    return controlGroupNoopObject;
  }
  let t7;
  if ($[25] !== clearFieldsOnDataImportEnabled || $[26] !== onCompanyDonationToggle || $[27] !== onIntervalSelectionChange || $[28] !== onPaymentMethodChange || $[29] !== onReceiptDesiredToggle || $[30] !== showAutoImportToggle || $[31] !== showDataImportAlert) {
    t7 = {
      showAutoImportToggle,
      showDataImportAlert,
      clearFieldsOnDataImportEnabled,
      onCompanyDonationToggle,
      onPaymentMethodChange,
      onReceiptDesiredToggle,
      onIntervalSelectionChange
    };
    $[25] = clearFieldsOnDataImportEnabled;
    $[26] = onCompanyDonationToggle;
    $[27] = onIntervalSelectionChange;
    $[28] = onPaymentMethodChange;
    $[29] = onReceiptDesiredToggle;
    $[30] = showAutoImportToggle;
    $[31] = showDataImportAlert;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
}

// TODO: Remove after A/B test -> https://betterplace.atlassian.net/browse/DEV-28639
export function useSetDataImportDefaultValues() {
  const $ = _c(8);
  const {
    setValue
  } = useDonationFormContext();
  const controlGroup = useFeatureFlagMatches(FeatureFlagKey.ExpressCheckout, "control");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["payment_method", "interval", "_company_donation"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [paymentMethod, interval, companyDonation] = useDonationFormValues(t0);
  let t1;
  let t2;
  if ($[1] !== companyDonation || $[2] !== controlGroup || $[3] !== interval || $[4] !== paymentMethod || $[5] !== setValue) {
    t1 = () => {
      if (controlGroup) {
        return;
      }
      if (paymentMethod === PaymentMethods.Paypal && interval === DonationIntervals.Single && !companyDonation) {
        setValue("import_data_from_psp", true, {
          shouldValidate: false
        });
        setValue("receipt_desired", true, {
          shouldValidate: false
        });
      }
    };
    t2 = [companyDonation, interval, paymentMethod, setValue, controlGroup];
    $[1] = companyDonation;
    $[2] = controlGroup;
    $[3] = interval;
    $[4] = paymentMethod;
    $[5] = setValue;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
}
export default useDataAutoImport;